import { createContext } from '@lit/context';
import { LitElement } from 'lit';

import { PrintOptionPersonalizationDto } from '@/api-sdk';
import { putSavePersonalization } from '@/api-sdk/clients';

export interface PersonalisationType extends PrintOptionPersonalizationDto {
  isCompleted?: boolean;
  printOptionCode: string;
  uploadedFileName?: string | null;
  hasDesignElements?: boolean;
}

export const personalisation = {
  context: createContext<PersonalisationType[]>(
    Symbol('personalisation-context'),
  ),
  /**
   * @param {PersonalisationType} personalisationContextArray - Current personalisation context array form consumer or provider
   * @param {string | undefined} printOptionCode - Print option code to find the personalisation data
   * @returns {PersonalisationType | undefined} - Returns personalisation data
   */
  getDataWithCode: (
    personalisationContextArray?: PersonalisationType[],
    printOptionCode?: string,
  ): PersonalisationType | undefined => {
    if (!personalisationContextArray) {
      return undefined;
    }
    return personalisationContextArray.find(
      personalisation => personalisation.printOptionCode === printOptionCode,
    );
  },
  /**
   *
   * @param personalisationData
   * @param personalisationContextArray
   * @returns
   */
  addPrintOptionData(
    personalisationData: PersonalisationType,
    personalisationContextArray: PersonalisationType[],
  ) {
    const { printOptionCode } = personalisationData;

    const personalisationIndex = personalisationContextArray.findIndex(
      personalisation => personalisation.printOptionCode === printOptionCode,
    );

    if (personalisationIndex === -1) {
      return (personalisationContextArray = [
        ...personalisationContextArray,
        personalisationData,
      ]);
    } else {
      return this.update(personalisationContextArray, {
        printOptionCode,
        personalisationData,
      });
    }
  },
  /**
   *
   * @param personalisationContextArray
   * @param object
   * @returns
   */
  update(
    personalisationContextArray: PersonalisationType[],
    {
      printOptionCode,
      personalisationData,
    }: {
      printOptionCode: string;
      personalisationData: Partial<PersonalisationType> | undefined;
    },
  ) {
    if (!personalisationData) {
      return this.remove(personalisationContextArray, printOptionCode);
    }

    return personalisationContextArray.map(personalisation =>
      personalisation.printOptionCode === printOptionCode
        ? { ...personalisation, ...personalisationData }
        : personalisation,
    );
  },
  /**
   *
   * @param personalisationContextArray
   * @param printOptionCode
   * @returns
   */
  remove(
    personalisationContextArray: PersonalisationType[],
    printOptionCode: string,
  ) {
    return personalisationContextArray.filter(
      personalisation => personalisation.printOptionCode !== printOptionCode,
    );
  },
  /**
   *
   * @param personalisationContextArray
   * @param configurationId
   * @param locale
   * @returns
   */
  async save(
    personalisationContextArray: PersonalisationType[],
    configurationId: string,
    locale?: string,
  ) {
    return Promise.allSettled(
      personalisationContextArray.map(personalisation =>
        putSavePersonalization({
          configurationId,
          printOptionCode: personalisation.printOptionCode,
          params: {
            locale,
          },
          data: personalisation,
        }),
      ),
    );
  },
  /**
   *
   * @param element
   * @param personalisationData
   * @param printOptionCode
   */
  providerUpdateEvent(
    element: LitElement,
    personalisationData: Partial<PersonalisationType>,
    printOptionCode?: string,
  ) {
    element.dispatchEvent(
      new CustomEvent('personalisation-context-update', {
        composed: true,
        detail: { personalisationData, printOptionCode },
      }),
    );
  },
  /**
   *
   * @param element
   * @param printOptionCode
   */
  providerRemoveEvent(element: LitElement, printOptionCode: string) {
    element.dispatchEvent(
      new CustomEvent('personalisation-context-update', {
        composed: true,
        detail: { printOptionCode, undefined },
      }),
    );
  },
  /**
   *
   * @param personalisationContextArray
   * @param personalisationData
   * @param printOptionCode
   * @returns
   */
  updateProviderListener(
    personalisationContextArray: PersonalisationType[],
    personalisationData: PersonalisationType,
    printOptionCode: string,
  ) {
    if (printOptionCode) {
      return this.update(personalisationContextArray, {
        printOptionCode,
        personalisationData,
      });
    }
    return this.addPrintOptionData(
      personalisationData,
      personalisationContextArray,
    );
  },

  /* check if the variant is already chosen so we know if we must 
    show the fg-personalisation-choose-visual-designer component */
  getChosenVariant(personalisationContextArray: PersonalisationType[]) {
    return personalisationContextArray?.[0]?.type;
  },
};
